import {
  integrationApi,
  projectApi,
  type Project,
  type ProjectMonitoringTerms,
} from '@shared/api-client';
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleDollarSign,
  Radio,
  Search,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import {
  mergeListeningTerms,
  normalizeTerm,
  uniqueTerms,
} from '@/lib/growth-signals';
import { revenueProvidersForSetup } from '@/lib/onboarding-integrations';
import { ConnectSearchConsoleDialog } from '@/pages/settings/components/connect-search-console-dialog';
import {
  ConnectRevenueCatDialog,
  ConnectStripeDialog,
} from '@/pages/settings/components/integrations-section';

const draftKey = (projectId: string) =>
  `gainsignal:growth-keywords:${projectId}`;
const readDraft = (projectId: string, suggestions: string[]) => {
  try {
    const value = JSON.parse(
      sessionStorage.getItem(draftKey(projectId)) ?? 'null',
    );
    if (
      value &&
      typeof value.text === 'string' &&
      value.text.length <= 1700 &&
      Array.isArray(value.selected) &&
      value.selected.every((term: unknown) => typeof term === 'string')
    ) {
      return value as { text: string; selected: string[] };
    }
  } catch {
    /* Storage can be unavailable; suggestions remain usable. */
  }
  return { text: suggestions.join('\n'), selected: [] as string[] };
};

export const GrowthSignals = ({ project }: { project: Project }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const savingKeywords =
    useIsMutating({ mutationKey: ['growth-listening', project.id] }) > 0;
  const heading = useRef<HTMLHeadingElement>(null);
  const [googleOpen, setGoogleOpen] = useState(false);
  const [revenueOpen, setRevenueOpen] = useState<
    'stripe' | 'revenuecat' | null
  >(null);
  useEffect(() => {
    heading.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
  }, []);
  const integrations = useQuery({
    queryKey: ['integrations', project.id],
    queryFn: () => integrationApi.listIntegrations(project.id),
    refetchOnWindowFocus: false,
  });
  const terms = useQuery({
    queryKey: ['project-monitoring-terms', project.id],
    queryFn: () => projectApi.getMonitoringTerms(project.id),
    refetchOnWindowFocus: false,
  });
  const suggestions = useQuery({
    queryKey: [
      'growth-keyword-suggestions',
      project.id,
      project.profile?.websiteUrl,
    ],
    queryFn: async () => {
      const result = await projectApi.discoverWebsite(
        project.profile!.websiteUrl!,
      );
      return {
        terms: result.suggestedMonitoringTerms ?? [],
        warning: result.warnings.join(' '),
      };
    },
    enabled:
      project.profile?.suggestedMonitoringTerms === undefined &&
      Boolean(project.profile?.websiteUrl),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const suggested =
    suggestions.data?.terms ?? project.profile?.suggestedMonitoringTerms ?? [];
  const suggestionPending =
    project.profile?.suggestedMonitoringTerms === undefined &&
    Boolean(project.profile?.websiteUrl) &&
    suggestions.isPending;
  const integrationConnected = (provider: string) =>
    integrations.data?.integrations.some(
      (item) =>
        item.provider === provider &&
        ['connected', 'backfilling', 'live'].includes(item.status),
    ) ?? false;
  const googleConnection = integrations.data?.integrations.find(
    (item) =>
      item.provider === 'google_search_console' &&
      ['connected', 'backfilling', 'live'].includes(item.status),
  );
  const revenueProviders = revenueProvidersForSetup(
    project.profile?.platform ?? 'web',
    project.profile?.trackingPlan?.revenue ?? 'none',
  );
  const revenueConnected = revenueProviders.some(integrationConnected);
  const finish = useMutation({
    mutationFn: async () => {
      const current = await projectApi.getProject(project.id);
      return projectApi.updateProject(project.id, {
        profile: { ...current.profile!, setupStep: 'complete' },
      });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['project', project.id], updated);
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      navigate(`/projects/${project.id}`, { replace: true });
    },
  });
  return (
    <div className="space-y-7">
      <div>
        <p className="mb-3 text-sm text-ink-muted">Optional · {project.name}</p>
        <h1
          ref={heading}
          tabIndex={-1}
          className="text-3xl font-semibold tracking-tight text-ink outline-none"
        >
          Add integrations
        </h1>
        <p className="mt-3 text-base leading-relaxed text-ink-muted">
          Your AI setup brief is ready. Connect the data sources you want, then
          add listening keywords for wider market signals.
        </p>
      </div>
      {revenueProviders.length > 0 && (
        <section
          aria-label="Revenue source"
          className="border-y border-border py-5"
        >
          <div className="flex items-start gap-3">
            <CircleDollarSign className="mt-0.5 size-5 shrink-0 text-ink-muted" />
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-ink">Revenue source</h2>
              <p className="mt-1 text-sm leading-relaxed text-ink-muted">
                Connect paid customers and revenue to product activity.
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 pl-8">
            {revenueProviders.map((provider) =>
              integrationConnected(provider) ? (
                <p
                  key={provider}
                  className="flex items-center gap-2 py-2 text-sm text-positive"
                >
                  <Check className="size-4" />
                  {provider === 'stripe' ? 'Stripe' : 'RevenueCat'} connected
                </p>
              ) : (
                <Button
                  key={provider}
                  variant="secondary"
                  disabled={
                    integrations.isPending || finish.isPending || savingKeywords
                  }
                  onClick={() => setRevenueOpen(provider)}
                >
                  Connect {provider === 'stripe' ? 'Stripe' : 'RevenueCat'}
                </Button>
              ),
            )}
          </div>
        </section>
      )}
      <section
        aria-label="Google Search Console"
        className={
          revenueProviders.length
            ? 'border-b border-border pb-5'
            : 'border-y border-border py-5'
        }
      >
        <div className="flex items-start gap-3">
          <Search className="mt-0.5 size-5 shrink-0 text-ink-muted" />
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-ink">
              Google Search Console
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-ink-muted">
              See the searches that bring people to your website.
            </p>
            {project.primaryDomain && (
              <p className="mt-2 break-words text-xs text-ink-muted">
                {project.primaryDomain}
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 pl-8">
          {googleConnection ? (
            <p className="flex items-center gap-2 text-sm text-positive">
              <Check className="size-4" /> Connected
              {googleConnection.status === 'backfilling'
                ? ' · importing search history'
                : ''}
            </p>
          ) : (
            <Button
              variant="secondary"
              disabled={
                integrations.isPending || finish.isPending || savingKeywords
              }
              onClick={() => setGoogleOpen(true)}
            >
              Connect Google
            </Button>
          )}
          {integrations.isError && (
            <p className="mt-2 text-sm text-ink-muted">
              Couldn’t check the connection.{' '}
              <button
                className="underline"
                onClick={() => void integrations.refetch()}
              >
                Retry
              </button>
            </p>
          )}
        </div>
      </section>
      <section
        aria-label="Additional listening keywords"
        className="space-y-4 border-b border-border pb-6"
      >
        <div className="flex items-start gap-3">
          <Radio className="mt-0.5 size-5 shrink-0 text-ink-muted" />
          <div>
            <h2 className="text-sm font-semibold text-ink">
              Listening keywords
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-ink-muted">
              Follow relevant public mentions and conversations beyond your
              brand.
            </p>
          </div>
        </div>
        {terms.isPending ? (
          <Skeleton className="h-24" />
        ) : terms.isError || !terms.data ? (
          <Alert tone="error">
            Couldn’t load listening settings.{' '}
            <button className="underline" onClick={() => void terms.refetch()}>
              Retry
            </button>
          </Alert>
        ) : (
          <>
            <p className="text-xs leading-relaxed text-ink-muted">
              Already monitored:{' '}
              {terms.data.automaticTerms.join(' · ') || project.name}
              {terms.data.customTerms.length
                ? ` · ${terms.data.customTerms.join(' · ')}`
                : ''}
            </p>
            {suggestionPending ? (
              <p role="status" className="py-3 text-sm text-ink-muted">
                Preparing suggested keywords…
              </p>
            ) : (
              <>
                {(suggestions.isError ||
                  suggestions.data?.warning ||
                  (!suggested.length && project.profile?.websiteUrl)) && (
                  <p className="text-sm text-ink-muted">
                    No more suggestions are ready yet. You can add your own
                    below or{' '}
                    <button
                      className="underline"
                      onClick={() => void suggestions.refetch()}
                    >
                      retry the scan
                    </button>
                    .
                  </p>
                )}
                <ListeningKeywords
                  projectId={project.id}
                  existing={terms.data}
                  suggestions={suggested}
                  disabled={finish.isPending}
                />
              </>
            )}
          </>
        )}
      </section>
      {finish.isError && <Alert tone="error">{finish.error.message}</Alert>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          variant="ghost"
          disabled={finish.isPending || savingKeywords}
          onClick={() => navigate(`/projects/${project.id}/setup`)}
        >
          <ArrowLeft /> Back to AI instructions
        </Button>
        <Button
          disabled={savingKeywords}
          loading={finish.isPending}
          onClick={() => finish.mutate()}
        >
          {googleConnection ||
          revenueConnected ||
          terms.data?.customTerms.length
            ? 'Open my dashboard'
            : 'Skip for now'}
          <ArrowRight />
        </Button>
      </div>
      <ConnectSearchConsoleDialog
        project={project}
        open={googleOpen}
        onOpenChange={setGoogleOpen}
        returnTo="setup"
      />
      <ConnectStripeDialog
        project={project}
        open={revenueOpen === 'stripe'}
        onOpenChange={(open) => !open && setRevenueOpen(null)}
      />
      <ConnectRevenueCatDialog
        project={project}
        open={revenueOpen === 'revenuecat'}
        onOpenChange={(open) => !open && setRevenueOpen(null)}
      />
    </div>
  );
};

const ListeningKeywords = ({
  projectId,
  existing,
  suggestions,
  disabled,
}: {
  projectId: string;
  existing: ProjectMonitoringTerms;
  suggestions: string[];
  disabled: boolean;
}) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(() =>
    readDraft(
      projectId,
      uniqueTerms(suggestions).filter(
        (term) =>
          ![...existing.automaticTerms, ...existing.customTerms].some(
            (saved) => normalizeTerm(saved) === normalizeTerm(term),
          ),
      ),
    ),
  );
  const [saved, setSaved] = useState(false);
  const edited = useRef(Boolean(draft.text.trim()));
  useEffect(() => {
    if (edited.current || !suggestions.length) {
      return;
    }
    const next = uniqueTerms(suggestions).filter(
      (term) =>
        ![...existing.automaticTerms, ...existing.customTerms].some(
          (saved) => normalizeTerm(saved) === normalizeTerm(term),
        ),
    );
    edited.current = true;
    setDraft({ text: next.join('\n'), selected: [] });
  }, [suggestions, existing]);
  const keywords = uniqueTerms(draft.text.split('\n'));
  const active = new Set(
    [...existing.automaticTerms, ...existing.customTerms].map(normalizeTerm),
  );
  const selected = keywords.filter(
    (term) => draft.selected.includes(term) && !active.has(normalizeTerm(term)),
  );
  useEffect(() => {
    try {
      sessionStorage.setItem(draftKey(projectId), JSON.stringify(draft));
    } catch {
      /* Optional local draft. */
    }
  }, [draft, projectId]);
  const enable = useMutation({
    mutationKey: ['growth-listening', projectId],
    mutationFn: async () => {
      const current = await projectApi.getMonitoringTerms(projectId);
      const terms = mergeListeningTerms(
        current.customTerms,
        selected,
        current.automaticTerms,
      );
      return projectApi.updateMonitoringTerms(projectId, { terms });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(['project-monitoring-terms', projectId], result);
      setSaved(true);
    },
  });
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-muted">
        These are suggestions. Choose keywords, then enable them below. Your
        draft is kept if you leave this step.
      </p>
      <fieldset disabled={disabled || enable.isPending} className="space-y-2">
        <legend className="sr-only">Choose additional keywords</legend>
        {keywords.map((term) => (
          <label
            key={term}
            className="flex cursor-pointer items-start gap-3 py-1 text-sm text-ink"
          >
            <input
              type="checkbox"
              checked={
                active.has(normalizeTerm(term)) || draft.selected.includes(term)
              }
              disabled={active.has(normalizeTerm(term))}
              onChange={(event) => {
                edited.current = true;
                setSaved(false);
                setDraft({
                  ...draft,
                  selected: event.target.checked
                    ? [...draft.selected, term]
                    : draft.selected.filter((item) => item !== term),
                });
              }}
              className="mt-0.5 size-4 shrink-0 accent-accent"
            />
            <span className="break-words">
              {term}
              {active.has(normalizeTerm(term)) && (
                <span className="ml-2 text-xs text-ink-muted">Enabled</span>
              )}
            </span>
          </label>
        ))}
        {!keywords.length && (
          <p className="text-sm text-ink-muted">
            Add a few focused phrases you want to follow.
          </p>
        )}
        <details
          className="text-sm text-ink-muted"
          open={!keywords.length || undefined}
        >
          <summary className="w-fit cursor-pointer py-2">Edit keywords</summary>
          <Field
            label="Additional keywords"
            htmlFor="growth-keywords"
            hint="One per line. Your existing listening keywords will be kept."
          >
            <textarea
              id="growth-keywords"
              value={draft.text}
              maxLength={1700}
              rows={5}
              onChange={(event) => {
                edited.current = true;
                setSaved(false);
                setDraft({
                  text: event.target.value,
                  selected: uniqueTerms(event.target.value.split('\n')),
                });
              }}
              className="w-full rounded-lg border border-border-strong bg-surface p-3 text-sm text-ink"
            />
          </Field>
        </details>
      </fieldset>
      {enable.isError && <Alert tone="error">{enable.error.message}</Alert>}
      {saved && (
        <p
          role="status"
          className="flex items-center gap-2 text-sm text-positive"
        >
          <Check className="size-4" /> Listening keywords enabled
        </p>
      )}
      <Button
        variant="secondary"
        disabled={disabled || !selected.length}
        loading={enable.isPending}
        onClick={() => enable.mutate()}
      >
        Enable selected keywords
      </Button>
    </div>
  );
};
